import React, { useEffect } from "react";
import { X, VideoCamera } from "@phosphor-icons/react";

function toEmbedUrl(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, "");
    if (host === "youtube.com" || host === "m.youtube.com") {
      const v = u.searchParams.get("v");
      if (v) return `https://www.youtube.com/embed/${v}?autoplay=1`;
    }
    if (host === "youtu.be") {
      const id = u.pathname.replace(/^\//, "");
      if (id) return `https://www.youtube.com/embed/${id}?autoplay=1`;
    }
    if (host === "vimeo.com") {
      const id = u.pathname.replace(/^\//, "");
      if (id) return `https://player.vimeo.com/video/${id}?autoplay=1`;
    }
  } catch (_err) { void _err; /* malformed URL — caller falls back to "Open video in a new tab" */ }
  return null;
}

const isDirectVideo = (url) => /\.(mp4|webm|mov|m4v|ogg)(\?|#|$)/i.test(url || "");

export default function VideoModal({ open, url, title, onClose }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  const embed = toEmbedUrl(url);
  const direct = isDirectVideo(url);

  return (
    <div
      className="fixed inset-0 z-[90] bg-ink-900/90 backdrop-blur-xl flex items-center justify-center px-4 py-10"
      onClick={onClose}
      data-testid="video-modal"
    >
      <button
        onClick={onClose}
        aria-label="Close video"
        className="absolute top-4 right-4 w-10 h-10 rounded-full bg-ink-800 border border-ink-600 text-white hover:text-neon-cyan hover:border-neon-cyan/60 flex items-center justify-center transition-colors z-10"
        data-testid="video-modal-close"
      >
        <X size={20} weight="bold" />
      </button>

      <div
        className="relative w-full max-w-5xl"
        onClick={(e) => e.stopPropagation()}
      >
        {title && (
          <div className="mb-3 text-center">
            <p className="text-[10px] font-bold tracking-[0.4em] text-neon-cyan uppercase mb-1 inline-flex items-center gap-2">
              <VideoCamera size={12} weight="fill" /> Video
            </p>
            <h3 className="text-xl sm:text-2xl font-black tracking-tight text-white">{title}</h3>
          </div>
        )}

        <div className="relative w-full aspect-video bg-black rounded-md overflow-hidden border border-ink-600">
          {embed ? (
            <iframe
              src={embed}
              title={title || "Video"}
              className="absolute inset-0 w-full h-full"
              frameBorder="0"
              allow="autoplay; encrypted-media; picture-in-picture"
              allowFullScreen
              data-testid="video-iframe"
            />
          ) : direct ? (
            <video
              src={url}
              controls
              autoPlay
              className="absolute inset-0 w-full h-full"
              data-testid="video-element"
            />
          ) : url ? (
            <div className="absolute inset-0 flex items-center justify-center text-zinc-500">
              <a href={url} target="_blank" rel="noopener noreferrer" className="text-neon-cyan underline">
                Open video in a new tab
              </a>
            </div>
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-zinc-500">No video</div>
          )}
        </div>
      </div>
    </div>
  );
}
